import { useEffect, useRef, useState } from 'react';
import { Icon } from '../../components/Icon';
import type { Photo } from '../../lib/api';
import { usePhotoUrls } from './usePhotoUrls';
import styles from './PhotoGrid.module.css';

const LAYOUT = ['', styles.one, styles.two, styles.three, styles.four];

interface Props {
  photos: Photo[];
  /** Used for alt text, e.g. "Photo 1 of 3 from Ben's note". */
  authorName: string | null;
}

export function PhotoGrid({ photos, authorName }: Props) {
  const { data: urls } = usePhotoUrls(photos);
  const [open, setOpen] = useState<number | null>(null);
  if (photos.length === 0) return null;
  const single = photos.length === 1;
  const alt = (i: number) =>
    photos.length === 1
      ? `Photo from ${authorName ?? 'a neighbor'}'s note`
      : `Photo ${i + 1} of ${photos.length} from ${authorName ?? 'a neighbor'}'s note`;

  return (
    <>
      <div className={[styles.grid, LAYOUT[photos.length]].join(' ')}>
        {photos.map((photo, i) => (
          <button
            key={photo.id}
            type="button"
            className={[styles.cell, single && styles.single].filter(Boolean).join(' ')}
            style={single ? { aspectRatio: `${photo.width} / ${photo.height}` } : undefined}
            onClick={() => setOpen(i)}
            aria-label={`${alt(i)}. Open larger.`}
          >
            {urls?.[photo.id] && <FadeImage src={urls[photo.id]!} alt="" />}
          </button>
        ))}
      </div>
      {open !== null && urls && (
        <Lightbox photos={photos} urls={urls} index={open} onIndex={setOpen} onClose={() => setOpen(null)} alt={alt} />
      )}
    </>
  );
}

function FadeImage({ src, alt }: { src: string; alt: string }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <img
      className={[styles.img, loaded && styles.loaded].filter(Boolean).join(' ')}
      src={src}
      alt={alt}
      loading="lazy"
      decoding="async"
      onLoad={() => setLoaded(true)}
    />
  );
}

interface LightboxProps {
  photos: Photo[];
  urls: Record<string, string>;
  index: number;
  onIndex: (i: number) => void;
  onClose: () => void;
  alt: (i: number) => string;
}

function Lightbox({ photos, urls, index, onIndex, onClose, alt }: LightboxProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const count = photos.length;
  const go = (delta: number) => onIndex((index + delta + count) % count);

  useEffect(() => {
    ref.current?.showModal();
  }, []);

  return (
    <dialog
      ref={ref}
      className={styles.lightbox}
      aria-label={alt(index)}
      onClose={onClose}
      onClick={(e) => {
        if (e.target === e.currentTarget || (e.target as HTMLElement).classList.contains(styles.stage!)) onClose();
      }}
      onKeyDown={(e) => {
        if (count > 1 && e.key === 'ArrowRight') go(1);
        if (count > 1 && e.key === 'ArrowLeft') go(-1);
      }}
    >
      <div className={styles.bar}>
        <span className={styles.counter}>{count > 1 ? `${index + 1} of ${count}` : ''}</span>
        <button type="button" className={styles.lbButton} onClick={onClose}>
          <Icon name="close" label="Close photo" />
        </button>
      </div>
      <div className={styles.stage}>
        <img className={styles.full} src={urls[photos[index]!.id]} alt={alt(index)} />
      </div>
      {count > 1 && (
        <div className={styles.nav}>
          <button type="button" className={styles.lbButton} onClick={() => go(-1)}>
            <Icon name="back" label="Previous photo" />
          </button>
          <button type="button" className={styles.lbButton} onClick={() => go(1)}>
            <Icon name="back" className={styles.flip} label="Next photo" />
          </button>
        </div>
      )}
    </dialog>
  );
}
